/* eslint-disable react/forbid-prop-types */
import { forwardRef, useMemo } from 'react';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import Chart from '../../charts';
import InsightCard from '../../insight';
import IframeCard from '../../iframe';
import Button from '../../../core/Button';
import TablePagination from '../../../core/TablePagination';

import filterByValues from '../../utils/formatCharts/filterByValues';
import groupByValues from '../../utils/formatCharts/groupByValues';
import KpiSkills from '../../utils/chartTools/KpiSkills';

import useOnScreen from '../../../hooks/useOnScreen';
// import checkDataSize from '../../utils/chartTools/kpiMsg';
import KpiStatus, { isKpiInvalidInStory, checkKpiLimit, allowValueModifiers } from '../../utils/chartTools/KpiStatus';

import {
  StyledCardKpi,
  StyledCardKpiTitle,
  StyledCardKpiContent,
  PaginationWrapper,
} from './styled/CardKpi.styled';

const getValueBackground = (isValue, globalConfig, background) => {
  if (isValue && globalConfig?.useGlobalTheme) return { backgroundColor: globalConfig.valueBg };
  return { backgroundColor: isValue ? background : 'transparent' };
};

const titleStyle = (tStyle, globalConfig) => ({
  fontSize: `${tStyle?.fontSize ?? 20}px`,
  color: globalConfig?.useGlobalTheme ? globalConfig.titles : (tStyle?.color ?? '#000000'),
  fontFamily: `'${(globalConfig?.useGlobalFont ? globalConfig.fontTitle : tStyle?.fontFamily) ?? 'arial'}', Arial`,
  fontWeight: tStyle?.bold ? 'bold' : 'normal',
  textDecoration: tStyle?.underline ? 'underline' : 'none',
  fontStyle: tStyle?.italic ? 'italic' : 'normal',
  textAlign: tStyle?.align ?? 'center',
  width: '100%',
  padding: '0 2px',
});

const getDynamicCardStyle = (styleConfig, drilldownSelected) => {
  if (drilldownSelected && styleConfig.DrilldownStyle && styleConfig.DrilldownStyle !== 'auto') {
    return styleConfig.DDStyles ?? styleConfig;
  }
  return styleConfig;
};

const emptyGlobalConfig = {
  useGlobalTheme: false, useGlobalFont: false,
};
const emptyState = [0, () => {}];

const CardKpi = forwardRef(({
  item,
  editPainel,
  drilldownSelected,
  removeDrilldown,
  id = undefined,
  stateDashboardLayout = undefined,
  handleStyleConfig = undefined,
  showToPrint = false,
  isDataLoading = false,
  globalConfig = emptyGlobalConfig,
  pageState = emptyState,
  collection = 'kpis',
  autoValueModifiers = null,
  handleDrilldownClick = null,
  onClick,
}, ref) => {
  const [page, setPage] = pageState;

  // Retorna se o kpi esta visivel na tela ou nao
  const isVisible = useOnScreen(ref);

  function modifyData(filterList, groupList, data, lineName) {
    let newData = data;
    if (filterList?.length > 0) {
      newData = filterByValues(filterList, newData, lineName);
    }
    if (groupList?.length > 0) {
      newData = groupByValues(groupList, newData, lineName);
    }
    return newData;
  }

  const data = useMemo(() => (
    item?.data?.data?.length > 0 ? (
      modifyData(
        item?.meta?.filterValues,
        item?.meta?.groupValues,
        item.data,
        item?.meta?.lines?.[0]?.column,
      )
    ) : (
      { index: [], data: [], columns: [] }
    )
  ), [item]);

  const handleAutoModifier = (modifType, ftype) => {
    if (item?.meta?.error === 'TooManyCategories') {
      autoValueModifiers(modifType, ftype, true, true);
    } else {
      autoValueModifiers(
        modifType,
        ftype,
        checkKpiLimit(
          item?.type,
          data?.columns?.length ?? 0,
        ),
        checkKpiLimit(
          item?.type,
          data?.index?.length ?? 0,
        ),
      );
    }
  };

  return (
    <StyledCardKpi
      ref={ref}
      titleFontSize={
        item?.style?.ShowTitleControl?.checked ? item?.style?.StyleTitleControl?.fontSize : 0
      }
      kpiType={item?.style?.type ?? item?.type}
      showTitle={item?.style?.ShowTitleControl?.checked}
      id={id}
      onClick={onClick}
      className="card-kpi"
      style={{
        borderRadius: `${Number.isNaN(+globalConfig?.borderStyle?.rounded) ? '4' : globalConfig.borderStyle.rounded}px`,
        overflow: [
          'label', 'smartLabel', 'Content', 'Iframe', 'Value', 'SubsetTable', 'Table',
        ].includes(item?.type) ? 'hidden' : 'visible',
      }}
    >
      {['label', 'smartLabel', 'Content'].includes(item?.type) && (
        <KpiStatus
          conditions={([
            [
              item?.meta?.error,
              item?.meta?.error?.length > 0,
            ],
          ])}
        >
          <InsightCard
            item={item}
            globalConfig={globalConfig}
          />
        </KpiStatus>
      )}
      {item?.type === 'Iframe' && (
        <KpiStatus
          conditions={([
            [
              'InvalidInStory',
              data && isKpiInvalidInStory(item?.linkType) && collection !== 'kpis',
            ],
          ])}
        >
          <IframeCard item={item} />
        </KpiStatus>
      )}
      {!['label', 'smartLabel', 'Iframe', 'Content'].includes(item?.type) && (
        <>
          {item?.style?.ShowTitleControl?.checked ? (
            <StyledCardKpiTitle
              style={getValueBackground((item?.style?.type ?? item?.type) === 'Value', globalConfig, item?.style?.GlobalValue?.background)}
            >
              <span
                style={titleStyle(item?.style?.StyleTitleControl, globalConfig)}
              >
                {item.name ?? ''}
              </span>
            </StyledCardKpiTitle>
          ) : <></>}
          <StyledCardKpiContent type={item?.type}>
            <KpiStatus
              isLoading={!(showToPrint || isVisible) || isDataLoading}
              conditions={([
                [
                  item?.meta?.error,
                  item?.meta?.error?.length > 0,
                ],
                [
                  'InvalidInStory',
                  data && isKpiInvalidInStory(item?.type) && collection !== 'kpis',
                ],
                [
                  'EmptyDataSet',
                  !data?.data?.length > 0 && item?.type !== 'Value',
                ],
                [
                  'TooManyCategories',
                  checkKpiLimit(
                    item?.type,
                    data?.columns?.length ?? 0,
                    data?.index?.length ?? 0,
                  ),
                ],
              ])}
              autoValueModifiers={
                allowValueModifiers(item?.type) && autoValueModifiers ? handleAutoModifier : null
              }
              drilldownSelected={drilldownSelected && editPainel}
              removeDrilldown={removeDrilldown}
              message={item?.meta?.errorMessage?.length > 0 ? item.meta.errorMessage : undefined}
            >
              <>
                {/* ========== Drilldown Back ========== */}
                {drilldownSelected && editPainel && (
                  <div
                    style={{
                      left: '10px',
                      position: 'absolute',
                      top: '10px',
                      zIndex: '1',
                    }}
                  >
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={removeDrilldown}
                    >
                      <span style={{ fontSize: '12px' }}>
                        Voltar
                      </span>
                    </Button>
                  </div>
                )}
                <Chart
                  id="svg"
                  type={item?.type}
                  handleDrilldownClick={handleDrilldownClick}
                  data={data}
                  valueFunction={item?.meta?.values?.[0]?.map}
                  styleConfig={getDynamicCardStyle(item?.style, drilldownSelected)}
                  kpiSize={stateDashboardLayout?.lg?.filter((d) => d?.i === item?.id)}
                  handleStyleConfig={(param1, param2) => handleStyleConfig(param2, param1)}
                  isEdit={editPainel}
                  globalConfig={globalConfig}
                />
              </>
            </KpiStatus>
            {isVisible && !!handleDrilldownClick && !showToPrint && (
              /**
               * Por enquanto apenas o drilldown.
               * Se tiver mais tem que fazer essa lista mais dinamica.
               * */
              <KpiSkills availableSkills={['drilldown']} />
            )}
          </StyledCardKpiContent>
          {item?.type === 'SubsetTable' && (
            <PaginationWrapper bgColor={globalConfig?.backgroundColor ?? '#ffffff'}>
              <TablePagination
                page={page}
                totalPages={(data?.total_pages ?? 1) - 1}
                setPage={setPage}
                useGoToPage={false}
                label=""
                className="tableWrapperPagination"
                disabled={editPainel}
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </StyledCardKpi>
  );
});

CardKpi.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  handleStyleConfig: PropTypes.func,
  editPainel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  id: PropTypes.string,
  showToPrint: PropTypes.bool,
  isDataLoading: PropTypes.bool,
  stateDashboardLayout: PropTypes.objectOf(PropTypes.any),
  globalConfig: PropTypes.objectOf(PropTypes.any),
  pageState: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  ),
  collection: PropTypes.string,
  autoValueModifiers: PropTypes.func,
  handleDrilldownClick: PropTypes.func,
  drilldownSelected: PropTypes.bool,
  removeDrilldown: PropTypes.func,
  onClick: PropTypes.func,
};

export default CardKpi;
